import { normalizeShortcutUrl } from './shortcutUrl';

export const MY_LINKS_TAB_ID = 'my-links';
export const SHORTCUTS_SCHEMA_VERSION = 1;
export const SCOPE_PERSONAL = 'personal';
export const SCOPE_SHARED = 'shared';
export const MAX_SHORTCUT_TABS = 20;
export const MAX_SHORTCUTS_PER_TAB = 80;
export const MAX_SHORTCUT_LABEL = 80;
export const MAX_TAB_TITLE = 40;
export const ICON_TYPE_CATALOG = 'catalog';
export const ICON_TYPE_IMAGE = 'image';
const ASSET_ID_RE = /^[a-f0-9]{32}$/;
const IMAGE_ADDRESS_FIELDS = [
  'imageUrl',
  'image_url',
  'sourceUrl',
  'source_url',
  'fetchUrl',
  'fetch_url',
  'iconUrl',
  'signedUrl',
  'downloadUrl',
  'objectPath',
  'stagingObjectPath',
  'uploadUrl',
  'upload_url',
];

function shortcutIconFields(raw, { strictIcons = false } = {}) {
  if (IMAGE_ADDRESS_FIELDS.some(key => raw?.[key])) {
    if (strictIcons) {
      const error = new Error('Image addresses are not accepted');
      error.code = 'remote_image_rejected';
      throw error;
    }
  }
  const storedType = String(raw?.iconType || ICON_TYPE_CATALOG).toLowerCase();
  const iconType = storedType === 'generate' ? ICON_TYPE_IMAGE : storedType;
  const iconKey = raw?.iconKey ? String(raw.iconKey).slice(0, 80) : null;
  if (iconType === ICON_TYPE_IMAGE) {
    const assetId = String(raw?.iconImageAssetId || '').trim().toLowerCase();
    if (!ASSET_ID_RE.test(assetId)) {
      if (strictIcons) {
        const error = new Error('This image cannot be used on the shortcut');
        error.code = 'invalid_asset';
        throw error;
      }
      return { iconType: ICON_TYPE_CATALOG, iconKey, iconImageAssetId: null };
    }
    return { iconType: ICON_TYPE_IMAGE, iconKey: null, iconImageAssetId: assetId };
  }
  return { iconType: ICON_TYPE_CATALOG, iconKey, iconImageAssetId: null };
}

export function utcNow() {
  return new Date().toISOString();
}

export function newEntityId(prefix) {
  const rand =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${rand}`;
}

export function defaultShortcutsPayload(tenantId, now = utcNow()) {
  return {
    schemaVersion: SHORTCUTS_SCHEMA_VERSION,
    tenantId: tenantId || null,
    scope: SCOPE_PERSONAL,
    tabs: [
      {
        id: MY_LINKS_TAB_ID,
        title: 'My Links',
        order: 0,
        system: true,
        scope: SCOPE_PERSONAL,
        createdAt: now,
        updatedAt: now,
      },
    ],
    shortcuts: [],
    updatedAt: now,
  };
}

function clipText(value, limit, fallback) {
  const text = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  return (text || fallback).slice(0, limit);
}

function sortByOrder(items) {
  return [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Canonical personal-shortcuts document. Keep this shape stable for a
 * future browser extension; do not store Home operational tiles here.
 */
export function coerceShortcutsPayload(data, tenantId, options = {}) {
  const now = utcNow();
  const base = defaultShortcutsPayload(tenantId, now);
  if (!data || typeof data !== 'object') return base;

  const tabsIn = Array.isArray(data.tabs) ? data.tabs : [];
  const shortcutsIn = Array.isArray(data.shortcuts) ? data.shortcuts : [];

  const tabs = [];
  const seenTabs = new Set();
  tabsIn.forEach((raw, idx) => {
    if (!raw || typeof raw !== 'object' || tabs.length >= MAX_SHORTCUT_TABS) return;
    if (String(raw.scope || SCOPE_PERSONAL) === SCOPE_SHARED) return;
    const id = String(raw.id || '').trim() || `tab_${idx}`;
    if (seenTabs.has(id)) return;
    seenTabs.add(id);
    tabs.push({
      id,
      title: clipText(raw.title, MAX_TAB_TITLE, 'Links'),
      order: idx,
      system: Boolean(raw.system) || id === MY_LINKS_TAB_ID,
      scope: SCOPE_PERSONAL,
      createdAt: raw.createdAt || now,
      updatedAt: raw.updatedAt || now,
    });
  });

  if (!tabs.some(tab => tab.id === MY_LINKS_TAB_ID)) {
    tabs.unshift({
      id: MY_LINKS_TAB_ID,
      title: 'My Links',
      order: 0,
      system: true,
      scope: SCOPE_PERSONAL,
      createdAt: now,
      updatedAt: now,
    });
  }
  tabs.forEach((tab, idx) => {
    tab.order = idx;
  });

  const tabIds = new Set(tabs.map(tab => tab.id));
  const shortcuts = [];
  const seen = new Set();
  const perTab = {};
  shortcutsIn.forEach((raw, idx) => {
    if (!raw || typeof raw !== 'object') return;
    if (String(raw.scope || SCOPE_PERSONAL) === SCOPE_SHARED) return;
    const url = normalizeShortcutUrl(raw.url);
    if (!url) return;
    let tabId = String(raw.tabId || MY_LINKS_TAB_ID);
    if (!tabIds.has(tabId)) tabId = MY_LINKS_TAB_ID;
    perTab[tabId] = perTab[tabId] || 0;
    if (perTab[tabId] >= MAX_SHORTCUTS_PER_TAB) return;
    const id = String(raw.id || '').trim() || `sc_${idx}`;
    if (seen.has(id)) return;
    seen.add(id);
    perTab[tabId] += 1;
    shortcuts.push({
      id,
      tabId,
      label: clipText(raw.label, MAX_SHORTCUT_LABEL, 'Link'),
      url,
      ...shortcutIconFields(raw, options),
      order: perTab[tabId] - 1,
      scope: SCOPE_PERSONAL,
      createdAt: raw.createdAt || now,
      updatedAt: raw.updatedAt || now,
    });
  });

  return {
    schemaVersion: SHORTCUTS_SCHEMA_VERSION,
    tenantId: tenantId || data.tenantId || null,
    scope: SCOPE_PERSONAL,
    tabs,
    shortcuts,
    updatedAt: data.updatedAt || now,
  };
}

export function defaultSharedShortcutsPayload(tenantId, now = utcNow()) {
  return {
    schemaVersion: SHORTCUTS_SCHEMA_VERSION,
    tenantId: tenantId || null,
    scope: SCOPE_SHARED,
    tabs: [],
    shortcuts: [],
    version: 0,
    updatedAt: now,
  };
}

export function sharedShortcutVersion(data) {
  const value = Number(data?.version);
  return Number.isInteger(value) && value >= 0 ? value : 0;
}

export function coerceSharedShortcutsPayload(data, tenantId, options = {}) {
  const now = utcNow();
  const empty = defaultSharedShortcutsPayload(tenantId, now);
  if (!data || typeof data !== 'object') return empty;
  const tabsIn = Array.isArray(data.tabs) ? data.tabs : [];
  const shortcutsIn = Array.isArray(data.shortcuts) ? data.shortcuts : [];
  const tabs = [];
  const seenTabs = new Set();
  tabsIn.forEach((raw, idx) => {
    if (!raw || typeof raw !== 'object' || tabs.length >= MAX_SHORTCUT_TABS) return;
    const id = String(raw.id || '').trim() || `stab_${idx}`;
    if (seenTabs.has(id) || id === MY_LINKS_TAB_ID) return;
    seenTabs.add(id);
    tabs.push({
      id,
      title: clipText(raw.title, MAX_TAB_TITLE, 'Links'),
      order: idx,
      system: false,
      scope: SCOPE_SHARED,
      createdAt: raw.createdAt || now,
      updatedAt: raw.updatedAt || now,
    });
  });
  const tabIds = new Set(tabs.map(tab => tab.id));
  const shortcuts = [];
  const seen = new Set();
  const perTab = {};
  shortcutsIn.forEach((raw, idx) => {
    if (!raw || typeof raw !== 'object') return;
    const url = normalizeShortcutUrl(raw.url);
    if (!url) return;
    const tabId = String(raw.tabId || '');
    if (!tabIds.has(tabId)) return;
    perTab[tabId] = perTab[tabId] || 0;
    if (perTab[tabId] >= MAX_SHORTCUTS_PER_TAB) return;
    const id = String(raw.id || '').trim() || `ssc_${idx}`;
    if (seen.has(id)) return;
    seen.add(id);
    perTab[tabId] += 1;
    shortcuts.push({
      id,
      tabId,
      label: clipText(raw.label, MAX_SHORTCUT_LABEL, 'Link'),
      url,
      ...shortcutIconFields(raw, options),
      order: perTab[tabId] - 1,
      scope: SCOPE_SHARED,
      createdAt: raw.createdAt || now,
      updatedAt: raw.updatedAt || now,
    });
  });
  return {
    schemaVersion: SHORTCUTS_SCHEMA_VERSION,
    tenantId: tenantId || data.tenantId || null,
    scope: SCOPE_SHARED,
    tabs,
    shortcuts,
    version: sharedShortcutVersion(data),
    updatedAt: data.updatedAt || now,
  };
}

export function mergeShortcutViews(personal, shared) {
  return {
    tabs: [
      ...(personal?.tabs || []).map(tab => ({ ...tab, scope: SCOPE_PERSONAL })),
      ...(shared?.tabs || []).map(tab => ({ ...tab, scope: SCOPE_SHARED })),
    ],
    shortcuts: [
      ...(personal?.shortcuts || []).map(item => ({ ...item, scope: SCOPE_PERSONAL })),
      ...(shared?.shortcuts || []).map(item => ({ ...item, scope: SCOPE_SHARED })),
    ],
  };
}

export function normalizeShortcutsBundle(data, tenantId) {
  if (data?.personal || data?.shared) {
    return {
      personal: coerceShortcutsPayload(data.personal, tenantId),
      shared: coerceSharedShortcutsPayload(data.shared, tenantId),
      canManageShared: Boolean(data.canManageShared),
    };
  }
  return {
    personal: coerceShortcutsPayload(data, tenantId),
    shared: defaultSharedShortcutsPayload(tenantId),
    canManageShared: Boolean(data?.canManageShared),
  };
}

export function shortcutsForTab(payload, tabId) {
  return sortByOrder((payload?.shortcuts || []).filter(item => item.tabId === tabId));
}

export function moveShortcutOrder(items, activeId, overId) {
  const next = sortByOrder(items);
  const from = next.findIndex(item => item.id === activeId);
  const to = next.findIndex(item => item.id === overId);
  if (from < 0 || to < 0 || from === to) return items;
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next.map((item, idx) => ({ ...item, order: idx, updatedAt: utcNow() }));
}

export function moveTabOrder(tabs, activeId, overId) {
  const next = sortByOrder(tabs);
  const from = next.findIndex(item => item.id === activeId);
  const to = next.findIndex(item => item.id === overId);
  if (from < 0 || to < 0 || from === to) return tabs;
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next.map((item, idx) => ({ ...item, order: idx, updatedAt: utcNow() }));
}

export function replaceTabShortcuts(payload, tabId, nextForTab) {
  const others = (payload.shortcuts || []).filter(item => item.tabId !== tabId);
  return {
    ...payload,
    shortcuts: [
      ...others,
      ...sortByOrder(nextForTab).map((item, idx) => ({ ...item, tabId, order: idx })),
    ],
    updatedAt: utcNow(),
  };
}
